import classNames from 'classnames';
import {
  forwardRef,
  type InputHTMLAttributes,
  useCallback,
  useId,
  useRef,
} from 'react';

import { Label } from '../label/Label';
import styles from './Switch.module.css';

type Size = 'sm' | 'md' | 'lg';

interface SwitchProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: string;
  inactiveLabel?: string;
  size?: Size;
  'data-testid'?: string;
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      label,
      inactiveLabel,
      size = 'md',
      disabled,
      className,
      style,
      id,
      'data-testid': dataTestId,
      ...inputProps
    },
    externalRef
  ) => {
    const generatedId = useId();
    const controlId = id ?? generatedId;
    const hasLabel = !!label;
    const labelId = `${controlId}-label`;
    const internalRef = useRef<HTMLInputElement | null>(null);

    const mergedRef = useCallback(
      (node: HTMLInputElement | null) => {
        internalRef.current = node;
        if (typeof externalRef === 'function') {
          externalRef(node);
        } else if (externalRef) {
          Object.assign(externalRef, { current: node });
        }
      },
      [externalRef]
    );

    return (
      <div
        className={classNames(
          styles.container,
          styles[size],
          disabled && styles.disabled,
          className
        )}
        style={style}
        data-testid={dataTestId}
      >
        {inactiveLabel && (
          <Label
            as="span"
            size={size}
            fontWeight="normal"
            disabled={disabled}
            onClick={() => internalRef.current?.click()}
          >
            {inactiveLabel}
          </Label>
        )}
        <input
          ref={mergedRef}
          type="checkbox"
          className={styles.input}
          id={controlId}
          disabled={disabled}
          aria-labelledby={hasLabel ? labelId : undefined}
          {...inputProps}
        />
        <label className={styles.track} htmlFor={controlId}>
          <span className={styles.thumb} />
        </label>
        {label && (
          <Label
            as="span"
            id={labelId}
            size={size}
            fontWeight="normal"
            disabled={disabled}
            onClick={() => internalRef.current?.click()}
          >
            {label}
          </Label>
        )}
      </div>
    );
  }
);

Switch.displayName = 'Switch';
